/** Arms the tracker after a successful widget load and exposes `analytics.track`. */

import { getChatApi } from "@/chatApi";
import { getNamespace } from "@/namespace";
import { sessionStorage } from "@/utils";

import { acquisitionFromLocation } from "./acquisition";
import {
  ensureDataLayerListener,
  installDataLayerListener,
  isValidEventType,
  resetDataLayerForTests,
  sanitizeEventPayload,
  stopDataLayerListener,
} from "./datalayer";
import { type ForwardedEvent } from "./forward";
import { visitorId } from "./identity";
import { installPageViews } from "./pageviews";
import {
  discardQueue,
  enqueue,
  flushQueue,
  type QueuedEvent,
  queuedEventTypes,
  shouldFlushNow,
} from "./queue";
import {
  createSession,
  readSession,
  type StoredSession,
  shouldStartNewSession,
  writeSession,
} from "./session";
import { uuidv7 } from "./uuid";

const FLUSH_INTERVAL_MS = 10_000;

/** Mode from the load that armed this tab. A rolled session repeats it. */
let loadedEmbed: boolean | undefined;

export interface ArmInput {
  loadSucceeded: boolean;
  disabled?: boolean;
  testMode?: boolean;
  /** Customer preview (`preview: 1`). Same as test mode: nothing is recorded. */
  preview?: boolean;
  /** Missing means enabled. Only an explicit false turns the tracker off. */
  trackingEnabled?: boolean;
  webdriver?: boolean;
  globalPrivacyControl?: boolean;
  doNotTrack?: string | null;
  killSwitch?: boolean;
}

let armedSite: string | null = null;
let currentSession: StoredSession | null = null;
let widgetMounted = false;
let listeners = false;
let timer: ReturnType<typeof setInterval> | null = null;
/** Set while a flush scans dataLayer so a full queue does not start a nested send. */
let deferImmediateFlush = false;

export function analyticsArmBlock(input: ArmInput): string | null {
  if (!input.loadSucceeded) return "load_failed";
  if (input.disabled) return "disabled";
  if (input.testMode) return "test_mode";
  if (input.preview) return "preview";
  if (input.trackingEnabled === false) return "tracking_disabled";
  if (input.webdriver) return "webdriver";
  if (input.globalPrivacyControl === true) return "gpc";
  if (input.doNotTrack === "1") return "dnt";
  if (input.killSwitch) return "kill_switch";
  return null;
}

/** Idempotent per site slug. A second load() does not record a second page view.
 * A different slug on the same page is refused, so one tracker's queue is not
 * flushed to the other site. */
export function startAnalytics(siteSlug: string, input: ArmInput): void {
  if (armedSite === siteSlug) return;
  if (armedSite !== null) return;
  const decision = analyticsArmBlock(resolveArmInput(siteSlug, input));
  if (decision) {
    discardQueue();
    if (input.trackingEnabled === false) writeKillSwitch(siteSlug);
    return;
  }
  armedSite = siteSlug;
  installRuntime();
  analyticsTrack("page_view");
  try {
    installDataLayerListener((event) => {
      if (armedSite) {
        analyticsTrack(event.type, { payload: event.payload });
      }
    });
  } catch {
    // Never let a locked host dataLayer abort widget load.
  }
}

export function setWidgetMounted(mounted: boolean): void {
  widgetMounted = mounted;
}

export function getSessionId(): string | null {
  if (!armedSite || !currentSession) return null;
  return currentSession.id;
}

/** Public `window[ns].analytics.track`. Calls before the tracker arms are dropped. */
export function analyticsTrack(
  type: string,
  props: Record<string, unknown> = {},
): void {
  if (!isValidEventType(type)) return;
  const session = beginEvent();
  if (!session) return;
  if (type === "widget_loaded") {
    enqueueLoadedOnce(session, props.embed);
    flushIfFull();
    return;
  }
  push(type, payloadOf(type, props), props);
  flushIfFull();
}

/** Widget events already mapped onto the allow-list. */
export function trackForwarded(event: ForwardedEvent): void {
  const session = beginEvent();
  if (!session) return;
  if (event.type === "widget_loaded") {
    enqueueLoadedOnce(session, event.payload?.embed);
    flushIfFull();
    return;
  }
  push(event.type, event.payload ?? {}, event);
  flushIfFull();
}

export function flushAnalytics(): Promise<void> {
  return flush(false);
}

export function resetAnalyticsForTests(): void {
  armedSite = null;
  currentSession = null;
  widgetMounted = false;
  loadedEmbed = undefined;
  discardQueue();
  resetDataLayerForTests();
  stopFlushTimer();
  deferImmediateFlush = false;
}

export { queuedEventTypes };

function resolveArmInput(siteSlug: string, input: ArmInput): ArmInput {
  return {
    loadSucceeded: input.loadSucceeded,
    disabled: input.disabled ?? false,
    testMode: input.testMode ?? getChatApi().chat.test_mode,
    preview: input.preview ?? getChatApi().chat.preview === true,
    trackingEnabled: input.trackingEnabled,
    webdriver: input.webdriver ?? navigator.webdriver === true,
    globalPrivacyControl:
      input.globalPrivacyControl ?? readGlobalPrivacyControl(),
    doNotTrack:
      input.doNotTrack !== undefined ? input.doNotTrack : readDoNotTrack(),
    killSwitch: input.killSwitch ?? killSwitchSet(siteSlug),
  };
}

function refreshSession(): boolean {
  if (!armedSite) return false;
  const now = Date.now();
  const acquisition = acquisitionFromLocation(
    window.location.href,
    document.referrer,
  );
  const stored = readSession(armedSite);
  if (!shouldStartNewSession(stored, now, acquisition.campaignKey) && stored) {
    stored.lastActivityAt = now;
    writeSession(armedSite, stored);
    currentSession = stored;
    return false;
  }
  currentSession = createSession(now, acquisition);
  writeSession(armedSite, currentSession);
  return true;
}

function beginEvent(): StoredSession | null {
  if (!armedSite) return null;
  const rolled = refreshSession();
  if (!currentSession) return null;
  if (rolled && widgetMounted) enqueueLoadedOnce(currentSession);
  return currentSession;
}

function enqueueLoadedOnce(session: StoredSession, embed?: unknown): void {
  if (session.widgetLoaded) return;
  session.widgetLoaded = true;
  if (armedSite) writeSession(armedSite, session);
  if (typeof embed === "boolean") loadedEmbed = embed;
  push(
    "widget_loaded",
    typeof loadedEmbed === "boolean" ? { embed: loadedEmbed } : {},
  );
}

function push(
  type: string,
  payload: Record<string, unknown>,
  target: { target_type?: string; target_id?: number } = {},
): void {
  if (!currentSession) return;
  const event: QueuedEvent = {
    id: uuidv7(),
    type,
    at: new Date().toISOString(),
    path: window.location.pathname,
    session: currentSession,
    attempts: 0,
  };
  if (Object.keys(payload).length > 0) event.payload = payload;
  if (
    typeof target.target_type === "string" &&
    typeof target.target_id === "number"
  ) {
    event.target_type = target.target_type;
    event.target_id = target.target_id;
  }
  enqueue(event);
}

function payloadOf(
  type: string,
  props: Record<string, unknown>,
): Record<string, unknown> {
  if (type === "page_view") {
    const title = document.title.slice(0, 200);
    return title ? { title } : {};
  }
  if (
    props.payload &&
    typeof props.payload === "object" &&
    !Array.isArray(props.payload)
  ) {
    const sanitized = sanitizeEventPayload(
      props.payload as Record<string, unknown>,
    );
    if (props.value !== undefined && sanitized.value === undefined) {
      const withValue = sanitizeEventPayload({ value: props.value });
      if (withValue.value !== undefined) sanitized.value = withValue.value;
    }
    return sanitized;
  }
  return sanitizeEventPayload(props);
}

function flushIfFull(): void {
  if (deferImmediateFlush) return;
  if (shouldFlushNow()) void flush(false);
}

function disarm(siteSlug: string): void {
  writeKillSwitch(siteSlug);
  armedSite = null;
  currentSession = null;
  discardQueue();
  stopDataLayerListener();
  stopFlushTimer();
}

function stopFlushTimer(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

async function flush(beacon: boolean): Promise<void> {
  if (!armedSite) return;
  deferImmediateFlush = true;
  try {
    ensureDataLayerListener();
  } catch {
    // A bad host dataLayer must not block the send.
  } finally {
    deferImmediateFlush = false;
  }
  let useBeacon = beacon;
  for (let attempt = 0; attempt < 60; attempt += 1) {
    if (!armedSite) return;
    const siteSlug = armedSite;
    const result = await flushQueue({
      siteSlug,
      visitorId: visitorId(siteSlug),
      beacon: useBeacon,
    });
    if (result.busy) return;
    if (result.trackingDisabled) {
      disarm(siteSlug);
      return;
    }
    // A failed fetch waits for a later flush, unless pagehide already
    // requested sendBeacon. The page is going away, so this turn has to
    // send the requeued batch. Other sessions still queued go out now.
    if (!result.followUp && (result.failed || !result.remaining)) return;
    useBeacon = useBeacon || result.followUp;
  }
}

function installRuntime(): void {
  if (!listeners) {
    listeners = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void flush(false);
    });
    window.addEventListener("pagehide", () => {
      void flush(true);
    });
    installPageViews(() => {
      if (armedSite) analyticsTrack("page_view");
    });
  }
  if (timer === null) {
    timer = setInterval(() => {
      void flush(false);
    }, FLUSH_INTERVAL_MS);
  }
}

function killKey(siteSlug: string): string {
  return `${getNamespace()}_analytics_off_${siteSlug}`;
}

function killSwitchSet(siteSlug: string): boolean {
  return sessionStorage.get(killKey(siteSlug)) === "1";
}

function writeKillSwitch(siteSlug: string): void {
  sessionStorage.set(killKey(siteSlug), "1");
}

function readGlobalPrivacyControl(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true;
}

function readDoNotTrack(): string | null {
  const fromWindow = (window as Window & { doNotTrack?: string | null })
    .doNotTrack;
  return navigator.doNotTrack ?? fromWindow ?? null;
}

getChatApi().analytics.track = (eventType, props) => {
  analyticsTrack(eventType, props ?? {});
};
